import { useCallback, useEffect, useState } from "react";
import {
  addCashMovement,
  closeCashRegister,
  fetchCashSummary,
  fetchOpenRegister,
  loadCashClosings,
  loadCashMovements,
  openCashRegister,
} from "@/lib/cashApi";
import type { CashClosing, CashMovement, CashRegister, CashSummary } from "@/types";

export interface CashActionResult {
  ok: boolean;
  error: string | null;
  register?: CashRegister;
  expected?: number;
  counted?: number;
  difference?: number;
}

export function useCashRegister(userId: string | null, branchId: string | null) {
  const [register, setRegister] = useState<CashRegister | null>(null);
  const [summary, setSummary] = useState<CashSummary | null>(null);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) {
      setRegister(null);
      setSummary(null);
      setMovements([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const current = await fetchOpenRegister(userId, branchId);
      setRegister(current);
      if (current) {
        const [sum, movs] = await Promise.all([
          fetchCashSummary(current.id),
          loadCashMovements(current.id),
        ]);
        setSummary(sum);
        setMovements(movs);
      } else {
        setSummary(null);
        setMovements([]);
      }
    } catch {
      setError("No se pudo cargar la información de caja.");
    } finally {
      setLoading(false);
    }
  }, [userId, branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const open = useCallback(
    async (openingAmount: number): Promise<CashActionResult> => {
      if (!branchId || !userId) {
        return { ok: false, error: "Selecciona una sucursal para abrir la caja." };
      }
      const result = await openCashRegister(branchId, userId, openingAmount);
      if (result.ok) await load();
      return { ok: result.ok, error: result.error, register: result.register };
    },
    [branchId, userId, load],
  );

  const move = useCallback(
    async (type: "ingreso" | "egreso", amount: number, concept: string): Promise<CashActionResult> => {
      if (!register || !userId) return { ok: false, error: "No hay caja abierta." };
      const result = await addCashMovement(register.id, userId, type, amount, concept);
      if (result.ok) await load();
      return { ok: result.ok, error: result.error };
    },
    [register, userId, load],
  );

  const close = useCallback(
    async (counted: number): Promise<CashActionResult> => {
      if (!register) return { ok: false, error: "No hay caja abierta." };
      const breakdown = summary
        ? {
            opening_amount: summary.opening_amount,
            cash_sales: summary.cash_sales,
            other_sales: summary.other_sales,
            income: summary.income,
            expense: summary.expense,
            sales_count: summary.sales_count,
          }
        : {};
      const result = await closeCashRegister(register.id, counted, breakdown);
      if (result.ok) await load();
      return {
        ok: result.ok,
        error: result.error,
        expected: result.expected,
        counted: result.counted,
        difference: result.difference,
      };
    },
    [register, summary, load],
  );

  return { register, summary, movements, loading, error, reload: load, open, move, close };
}

export function useCashClosings() {
  const [closings, setClosings] = useState<CashClosing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await loadCashClosings();
      setClosings(result.closings);
      setError(result.error);
    } catch {
      setError("No se pudo cargar el historial de cierres.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { closings, loading, error, reload: load };
}